import type { TopLevelNavCategoryItem } from '../types';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';

import { memo } from 'react';

import { S } from '../TopLevelNavigation.styled';
import { getCategoryLabelId } from '../utils';

/**
 * Handlers take the category index so the parent can pass the same (memoized) function to
 * every category; per-category closures would defeat `memo`.
 */
type TopLevelNavigationCategoryProps = {
  category: TopLevelNavCategoryItem;
  index: number;
  isActive: boolean;
  isNavigationOpen: boolean;
  hasOverlay: boolean;
  panelId: string;
  onMouseEnter: (index: number) => void;
  onMouseMove: (index: number) => void;
  onClickLink: (index: number, event: MouseEvent<HTMLAnchorElement>) => void;
  onFocusLink: () => void;
  onKeyDownLink: (event: KeyboardEvent<HTMLAnchorElement>) => void;
  onPointerDownLink: (index: number, event: PointerEvent<HTMLAnchorElement>) => void;
  onClickToggle: (index: number, currentTarget: HTMLElement) => void;
  onKeyDownToggle: (index: number, event: KeyboardEvent<HTMLButtonElement>) => void;
};

const TopLevelNavigationCategoryComponent = ({
  category,
  index,
  isActive,
  isNavigationOpen,
  hasOverlay,
  panelId,
  onMouseEnter,
  onMouseMove,
  onClickLink,
  onFocusLink,
  onKeyDownLink,
  onPointerDownLink,
  onClickToggle,
  onKeyDownToggle,
}: TopLevelNavigationCategoryProps) => {
  const { label } = category;
  const labelId = getCategoryLabelId(index);
  const categoryLabel = (
    <S.CategoryLabel $active={isActive} $navigationOpen={isNavigationOpen}>
      {label}
    </S.CategoryLabel>
  );

  return (
    <S.Category onMouseEnter={() => onMouseEnter(index)} onMouseMove={() => onMouseMove(index)}>
      {category.url ? (
        <S.CategoryLink
          id={labelId}
          data-testid="top-level-navigation-category-link"
          $first={index === 0}
          href={category.url}
          target={category.target}
          onClick={event => onClickLink(index, event)}
          onFocus={onFocusLink}
          onKeyDown={onKeyDownLink}
          onPointerDown={event => onPointerDownLink(index, event)}
        >
          {categoryLabel}
        </S.CategoryLink>
      ) : (
        <S.CategoryText
          id={labelId}
          data-testid="top-level-navigation-category-text"
          $first={index === 0}
        >
          {categoryLabel}
        </S.CategoryText>
      )}
      {hasOverlay && (
        <S.Toggle
          id={`top-level-navigation-toggle-${index}`}
          data-testid="top-level-navigation-toggle"
          type="button"
          // Named by the category; the open/closed state comes from aria-expanded.
          aria-labelledby={labelId}
          aria-controls={panelId}
          aria-expanded={isActive}
          aria-haspopup="true"
          onClick={event => onClickToggle(index, event.currentTarget)}
          onKeyDown={event => onKeyDownToggle(index, event)}
        />
      )}
    </S.Category>
  );
};

export const TopLevelNavigationCategory = memo(TopLevelNavigationCategoryComponent);
